import React, { useEffect, useState, useCallback } from 'react';
import useProtecaoDeRota from '../hooks/useProtecaoDeRota';
import { Spin } from 'antd';
import { jwtDecode } from 'jwt-decode';
import useApi from '../hooks/useApi';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Star,
    StarBorderOutlined
} from "@mui/icons-material";

const FavoriteButton = ({ dashboardId, isFavorite, onToggle }) => {
    const { callApi, loading } = useApi(
        `https://vision.alfama.com/api/update/dashboards/favorito/${dashboardId}`,
        {
            method: 'GET',
            includeToken: true,
            successMessage: 'Favorito atualizado!',
            errorMessage: 'Erro ao atualizar favorito'
        }
    );

    const handleFavoriteClick = async (e) => {
        e.stopPropagation();
        if (loading) return;

        try {
            await callApi();
            onToggle(dashboardId, isFavorite);
        } catch (error) {
            console.error("Erro no handleFavoriteClick:", error);
        }
    };

    return (
        <button
            onClick={handleFavoriteClick}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-yellow-500 transition-colors disabled:opacity-50"
            aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
            {isFavorite ? <Star className="text-yellow-500" /> : <StarBorderOutlined />}
        </button>
    );
};


const PaginaFavoritos = () => {
    const verificando = useProtecaoDeRota();
    const navigate = useNavigate();
    const [dashboards, setDashboards] = useState([]);
    const [groupedFavorites, setGroupedFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDepartments, setOpenDepartments] = useState(new Set());

    const token = localStorage.getItem('token');
    const decodedToken = jwtDecode(token);
    const userId = decodedToken.usuario_id;
    const nomeUsuario = decodedToken.nome || 'Usuário';
    const departamentoUsuario = decodedToken.departamento?.nome;

    const { callApi: fetchDashboardsApi } = useApi(`https://vision.alfama.com/api/view/usuarios/dashboard/${userId}`, { includeToken: true });

    const handleToggleFavoriteState = useCallback((dashboardId) => {
        setDashboards(currentDashboards =>
            currentDashboards.filter(d => d.dashboard_id !== dashboardId)
        );
    }, []);

    const groupDashboards = useCallback((dashboardList) => {
        const groupedData = dashboardList.reduce((acc, item) => {
            const dept = item.nome_departamento;
            if (!acc[dept]) acc[dept] = [];
            acc[dept].push({
                id: item.dashboard_id,
                label: item.nome_dashboard,
                description: item.descricao_dashboard,
                path: `/relatorio/${item.link_id}`,
                is_homologado: item.is_homologado,
                is_favorito: item.is_favorito === 1,
            });
            return acc;
        }, {});

        const formattedDepartamentos = Object.entries(groupedData)
            .map(([nome, cards]) => ({
                nome,
                cards,
                isUserDepartment: nome === departamentoUsuario
            }))
            .sort((a, b) => {
                if (a.isUserDepartment && !b.isUserDepartment) return -1;
                if (!a.isUserDepartment && b.isUserDepartment) return 1;
                return a.nome.localeCompare(b.nome);
            });

        setGroupedFavorites(formattedDepartamentos);
    }, [departamentoUsuario]);

    useEffect(() => {
        const fetchAndFilterFavorites = async () => {
            try {
                setLoading(true);
                const response = await fetchDashboardsApi();
                if (response) {
                    const favorited = response.filter(item => item.is_favorito === 1);
                    setDashboards(favorited);
                    groupDashboards(favorited);
                    const deptNames = new Set(favorited.map(d => d.nome_departamento));
                    setOpenDepartments(deptNames);
                }
            } catch (err) {
                console.error('Erro ao buscar dashboards:', err);
                setError('Não foi possível carregar seus favoritos.');
            } finally {
                setLoading(false);
            }
        };
        if (userId) fetchAndFilterFavorites();
    }, [userId, fetchDashboardsApi, groupDashboards]);

    useEffect(() => {
        groupDashboards(dashboards);
    }, [dashboards, groupDashboards]);


    const handleDepartmentClick = (deptNome) => {
        setOpenDepartments(prev => {
            const newState = new Set(prev);
            newState.has(deptNome) ? newState.delete(deptNome) : newState.add(deptNome);
            return newState;
        });
    };

    if (verificando || loading) {
        return <div className="flex items-center justify-center min-h-[50vh]"><Spin size="large" tip="Carregando seus favoritos..." /></div>;
    }

    return (
        <>
            <Helmet><title>Vision | Favoritos</title></Helmet>
            <main className="flex-1 w-full px-4 sm:px-8 py-6 mx-auto bg-gray-50">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Seus Favoritos, {nomeUsuario}!</h1>
                    <p className="text-md text-gray-500">Acesse seus relatórios e dashboards favoritos abaixo</p>
                </div>

                {error && <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg">{error}</div>}

                {!loading && !error && dashboards.length === 0 && (
                    <div className="text-center text-gray-500 p-4 bg-white rounded-lg border">
                        <h2 className="text-xl font-semibold">Nenhum favorito disponível</h2>
                        <p>Você ainda não marcou nenhum dashboard como favorito. Navegue pela página inicial e clique na estrela para adicionar.</p>
                    </div>
                )}

                {!loading && !error && groupedFavorites.length > 0 && (
                    <div className="space-y-3">
                        {groupedFavorites.map((dept) => (
                            <div key={dept.nome} className="bg-white rounded-lg border border-gray-200">
                                <div onClick={() => handleDepartmentClick(dept.nome)} className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition">
                                    <div className="flex items-center gap-4">
                                        <h3 className="text-lg font-semibold text-gray-700">{dept.nome}</h3>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500">{dept.cards.length} {dept.cards.length === 1 ? 'item' : 'itens'}</span>
                                        {openDepartments.has(dept.nome) ? <KeyboardArrowUp className="text-gray-500" /> : <KeyboardArrowDown className="text-gray-500" />}
                                    </div>
                                </div>

                                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${openDepartments.has(dept.nome) ? "max-h-[2000px]" : "max-h-0"}`}>
                                    <div className="p-4 border-t border-gray-200">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {dept.cards.map(dashboard => (
                                                <div key={dashboard.id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-alfama-blue transition flex flex-col relative overflow-hidden">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1 pr-2 cursor-pointer" onClick={() => navigate(dashboard.path)}>
                                                            <h4 className="font-semibold text-gray-800 truncate">{dashboard.label}</h4>
                                                        </div>
                                                        <FavoriteButton
                                                            dashboardId={dashboard.id}
                                                            isFavorite={dashboard.is_favorito}
                                                            onToggle={handleToggleFavoriteState}
                                                        />
                                                    </div>
                                                    <div className="flex-1 cursor-pointer" onClick={() => navigate(dashboard.path)}>
                                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 flex-grow">{dashboard.description}</p>
                                                    </div>
                                                    {dashboard.is_homologado === 1 && (
                                                        <div className="absolute top-2 -right-10 bg-yellow-400 text-white text-xs font-bold px-10 py-1 rotate-45 z-10">HOMOLOGAÇÃO</div>
                                                    )}
                                                    <span onClick={() => navigate(dashboard.path)} className="text-xs font-bold text-alfama-blue mt-3 inline-block cursor-pointer">Acessar →</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </>
    );
};

export default PaginaFavoritos;